// ============================================================================
// adRewardJourney — pure state machine for the "Double Rewards" ad gauntlet
// ----------------------------------------------------------------------------
// Spec §4B. The user picks "Double Rewards"; we capture the base payout into
// `pendingSyncValue` (dropout-safe), stream N sequential ads, and only on a
// clean run through every index do we double. A blur/close/refresh mid-run
// drops the bonus and recovers the base. All pure + firebase-free; the timed
// ad player and Firestore commit live in the component/service.
// ============================================================================

import { adsRequiredToDouble, DOUBLE_REWARD_MULTIPLIER } from './gamificationRegistry'

export type AdJourneyPhase = 'IDLE' | 'PLAYING' | 'COMPLETED' | 'DROPPED'

export interface AdJourneyState {
  phase: AdJourneyPhase
  baseValue: number
  isRareDrop: boolean
  totalAds: number
  adsWatched: number
  /** Base payout captured at journey start — what we recover on a dropout. */
  pendingSyncValue: number
}

export const createAdJourney = (baseValue: number, isRareDrop = false): AdJourneyState => {
  const safeBase = Math.max(0, Math.floor(baseValue))
  return {
    phase: 'IDLE',
    baseValue: safeBase,
    isRareDrop,
    totalAds: adsRequiredToDouble(safeBase, isRareDrop),
    adsWatched: 0,
    pendingSyncValue: safeBase,
  }
}

/** Locks the journey into PLAYING (the double button is destroyed by the UI). */
export const startAdJourney = (state: AdJourneyState): AdJourneyState =>
  state.phase === 'IDLE' ? { ...state, phase: 'PLAYING' } : state

/** Marks one ad index complete; transitions to COMPLETED on the final index. */
export const completeAd = (state: AdJourneyState): AdJourneyState => {
  if (state.phase !== 'PLAYING') return state
  const adsWatched = state.adsWatched + 1
  if (adsWatched >= state.totalAds) {
    return { ...state, adsWatched: state.totalAds, phase: 'COMPLETED' }
  }
  return { ...state, adsWatched }
}

/** Abrupt dropout (blur/close/refresh) — bonus forfeited unless already done. */
export const dropAdJourney = (state: AdJourneyState): AdJourneyState =>
  state.phase === 'COMPLETED' ? state : { ...state, phase: 'DROPPED' }

export const isAdJourneyComplete = (state: AdJourneyState): boolean => state.phase === 'COMPLETED'

export const isAdJourneySettled = (state: AdJourneyState): boolean =>
  state.phase === 'COMPLETED' || state.phase === 'DROPPED'

export const remainingAds = (state: AdJourneyState): number =>
  Math.max(0, state.totalAds - state.adsWatched)

/**
 * Credits to commit for the settled journey: doubled on a clean completion,
 * otherwise the recovered base (`pendingSyncValue`).
 */
export const adJourneyPayout = (state: AdJourneyState): number =>
  isAdJourneyComplete(state) ? state.baseValue * DOUBLE_REWARD_MULTIPLIER : state.pendingSyncValue

/** Whether the settled journey earned the doubled bonus (for the commit call). */
export const adJourneyDoubled = (state: AdJourneyState): boolean => isAdJourneyComplete(state)
